import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type CSSProperties, type ForwardedRef, type PointerEvent as ReactPointerEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Check, GripVertical, Mic, Music, Plus, Trash2 } from 'lucide-react'
import type { Card } from '../../types'
import type { AppTranslations } from '../../i18n/app'
import { findTermSide, isCustomNamedSide } from '../../utils/cardSides'
import { hasValue, valueSide } from '../../utils/fieldValue'
import { notInStudyKind, type LearnGapField } from '../../utils/cardSideDisplay'
import { useLocale } from '../../i18n/context'
import { useSpeech } from '../../hooks/useSpeech'
import { useCreateCard, useUpdateCard } from '../../hooks/useCards'
import { useCardSenses, useFillCard, useSwitchCardSense, type CardSenseCandidate } from '../../hooks/useFill'
import { translationProvenance } from '../../utils/meaningChooser'
import { extractErrorMessage } from '../../api/client'
import {
  buildSidesFromDraft, draftSignature, draftToCardSides, draftsFromCardSides,
  droppedFieldIdentities, fillEmptyFieldsFromSense, hasAuthoredDefinition, newFieldKey,
  reconcileDraftWithServer, restoreBlankCustomLabel,
  serverSideSignature, STANDARD_LABEL, type DraftTouch, type FieldDraft,
} from '../../utils/cardEditorFields'
import {
  freshestCard, getCardDraftTouch, getCardSnapshot, markCardWriteFailed,
  pendingCardWrite, publishCardDraft, retractCardDraft, subscribeCardSnapshot,
  visibleCard,
} from '../../lib/cardSnapshots'
import { useCardWriteFailed } from '../../hooks/useCardDisplay'
import { TERM_FIELD_KEY } from './VocabCard'
import { TERM_FIELD_TYPE, fieldTypeOfDraft, type FieldType } from '../../utils/deckCardNavigation'
import {
  authorityChanges, moveField, settledSpans, stepTowardPointer, tileSpans, type MeasuredRow,
} from '../../utils/cardFieldReorder'
import { computeFlipDeltas, translateYOf } from '../../utils/rowFlip'
import ImageSideInput from './ImageSideInput'
import { isImageContent } from '../../utils/imageContent'
import { compressImageForStorage } from '../../services/imageCompress'
import { toast } from './Toast'
import AudioSideInput from './AudioSideInput'
import NotInStudyMark from './NotInStudyMark'
import UnsavedMark from './UnsavedMark'
import MeaningChooser, { NoDictionaryLinkTag } from './MeaningChooser'

/**
 * The card's third state: ACTIVE — editing mode only (brief §5.3, ADR-067
 * Decisions 2–5). Text is edited where it lives; there is no composer and no
 * shared input elsewhere on the screen.
 *
 * What this component is responsible for, and what it deliberately is not:
 *
 *  • It owns the draft for exactly one card and commits it. Committing happens
 *    on leaving a field, on unmount (which is how "Done" and "activate another
 *    card" both flush an in-flight edit — see the caller), and on a field
 *    deletion. Nothing else writes.
 *  • Field ORDER is the draft's own array order. Drafts are built from stored
 *    position order and written back in that order, so editing the second
 *    Example can never promote it past the first (ADR-066 Decision 4) — no
 *    edit, insertion or deletion moves a field. The ONE thing that moves a
 *    field is the drag handle on its label band (brief §14): order is
 *    semantic, so the affordance that sets it is first-class, and a move that
 *    changes which field of a repeated kind comes first is reported through
 *    `onFieldOrderChanged` because it changed what Learn asks (ADR-066
 *    Decision 1). Term has neither handle nor delete and its gutter is left
 *    empty rather than absent — §5.3's statement that it cannot move.
 *  • It never TRIGGERS enrichment as a side effect. Fill stays pull-only
 *    (ADR-063 amendment) — creating, editing and saving a field are all
 *    silent about it. Fill itself now lives here, in a row at the end of the
 *    field stack, alongside `+ Field` (ADR-067 Decision B moved both off the
 *    deck-scoped editing bar because both act on exactly one card). It is
 *    still a single deliberate tap that runs only on explicit press — the
 *    same call the read-only expanded card makes (VocabCard), not a second
 *    entry point with its own rules.
 *  • `+ Field` (brief §11) is reached here via an imperative handle plus a
 *    toggle callback: the caller still owns the type-chooser POPOVER (it
 *    lives in its own fixed slot above the editing bar, unmoved by Decision
 *    B — only the trigger that opens it moved onto this card), but the field
 *    itself — key, position, focus — belongs to this component's own draft,
 *    the same as every other field. Insertion always APPENDS to side B, never
 *    touches existing order (ADR-066 Decision 4), and a field left empty on
 *    blur or on unmount is discarded with no trace and no Undo — it never
 *    existed as far as the stored card is concerned.
 *  • It handles BOTH an existing card and a brand-new unsaved one (brief §10).
 *    A new card is passed in with an empty `id` and no sides; the first commit
 *    that has a non-blank Term POSTs it and every commit after that PATCHes the
 *    id the server returned. This is why "a card whose Term is still empty is
 *    discarded silently" needs no delete request and no cleanup: an unsaved
 *    draft was never written, so dropping the component IS the discard. The
 *    card's stored POSITION is never sent — the server appends at
 *    `max(position) + 1` over the whole deck, so a partially loaded feed
 *    cannot make a new card land in the middle (the client-side-deck-operation
 *    defect family in the brief's open tail #9).
 *  • Previous/Next and the card overflow menu are not here: each belongs to its
 *    own step — Previous/Next is the editing bar's (mobile) and ⇥/⇧⇥'s
 *    (desktop), both walking the shown set and carrying the caret's field type.
 *  • THE CAPABILITIES THAT USED TO NEED THE FULL-SCREEN EDITOR LIVE HERE NOW:
 *    the meaning arrows (paging a card's corpus sense), replacing an image or
 *    audio file, and renaming a custom field's label after creation. Each was
 *    reachable from exactly one surface before, which is why they were moved
 *    into this component rather than rebuilt per surface — the phone's active
 *    card and the desktop right panel are both this file.
 */

// DESIGN_SYSTEM.md "Field drag" (Animation table) — one duration/easing pair
// shared by the lift halo's fade and the neighbour slide (FLIP), so the two
// read as one motion rather than two gestures that happen to coincide.
const FIELD_DRAG_LIFT_MS = 150
const FIELD_DRAG_SLIDE_MS = 150
const FIELD_DRAG_EASING = 'ease-out'
// How long a KEYBOARD move holds the lift once it lands, on top of the fade
// itself — long enough to read as a deliberate confirmation, not a flicker. A
// pointer drag has no equivalent constant: it holds the lift for exactly as
// long as the finger does.
const KEYBOARD_LIFT_HOLD_MS = 250

export interface ActiveVocabCardHandle {
  /** Appends an empty field of `kind` to side B and focuses it (brief §11).
   * `label` is only meaningful for `kind === 'note'` (the Custom type) —
   * every other kind uses its fixed STANDARD_LABEL. */
  addField: (kind: string, label?: string) => void
  /**
   * Puts the caret in an already-rendered field (desktop, brief §22: "clicking
   * a field in a row selects the card, opens it on the right, and places the
   * caret in the SAME field").
   *
   * It exists because `initialFieldKey` is read once, on mount, and the desktop
   * right panel does NOT remount when the reader clicks a different field of
   * the card it is already showing — that is the common case, not the edge one.
   * Same path as every other focus: it calls `.focus()` on the registered
   * input, so `handleFocus` runs and the field is highlighted by the same rule
   * a tap uses. A key naming no rendered field is a no-op rather than an error;
   * the caller may be pointing at a field a concurrent edit removed.
   */
  focusField: (key: string, caret?: number | null) => void
}

interface Props {
  /** An existing card, or a synthetic one with `id: ''` for an unsaved new
   * card (brief §10) — see the create/update split in `commit`. */
  card: Card
  /** Target deck for the POST when `card.id` is empty. */
  deckId: string
  /** Reports the server's card once an unsaved draft has been created, so the
   * caller can stop double-rendering it against the refetched list. */
  onCreated?: (card: Card) => void
  deckLanguage: string
  /** Bare position number for the header row — §5.3: no "of 150", the bar owns totals. */
  cardNumber: number
  /** Field to focus on mount, from the tap that activated this card. */
  initialFieldKey: string | null
  /** Caret offset inside that field; null means "at the end". */
  initialCaret: number | null
  /**
   * Opens/closes the `+ Field` type chooser (brief §11). The popover itself
   * stays in the caller's own fixed slot above the editing bar (ADR-067
   * Decision B moves only the trigger, not the chooser's placement or its
   * measured bottom reserve) — this only toggles whether it is showing, the
   * same callback the editing bar used to own.
   */
  onToggleFieldChooser: () => void
  /** Whether the chooser this card's `+ Field` trigger opens is currently
   * showing, so the trigger can report `aria-expanded` correctly. */
  fieldChooserOpen: boolean
  /**
   * Reports which field row currently holds the caret, with both the row
   * element and the card element that contains it, so the caller can apply the
   * §7 positioning rule. The ROW, not the text area, so the field's label stays
   * visible (§7 rule 4) — and the CARD too, because ADR-067 Decision A makes
   * the card the unit of positioning: the neighbouring elements that must stay
   * reachable belong to the card, not to the field, and the action row at the
   * end of the field stack is one of them.
   */
  onFieldFocus: (row: HTMLElement, card: HTMLElement | null) => void
  onFieldBlur: () => void
  /**
   * Reports the TYPE of the field that just took the caret, so the caller can
   * carry it to the next card (brief §13's field-type preservation). Read from
   * this card's own draft rather than from the stored side, because a field
   * added this session via `+ Field` has no stored side yet and a walk started
   * from one must still hold its type.
   */
  onFocusedFieldType?: (type: FieldType) => void
  /**
   * Set when a walk landed here holding a field type this card does not have
   * (brief §13). Renders `+ Add Example` in the field's place; pressing it
   * creates the field and focuses it. `kind`/`label` are passed straight to the
   * same `addField` the `+ Field` chooser calls, so the two paths cannot
   * diverge on what creating a field of that type means.
   */
  missingFieldType?: { kind: string; label?: string; displayName: string } | null
  /** A filled field was removed — the caller shows the transient Undo (§12). */
  onFieldDeleted: (info: { label: string; restore: () => void }) => void
  /**
   * A reorder handed a repeated kind's authority to a different field, so Learn
   * now prompts from another one (ADR-066 Decision 1). Fires only for that case
   * — a move that rearranges the card without changing any kind's first field
   * says nothing, because nothing about study changed. The labels are this
   * card's own displayed ones, in the order they now appear; the caller words
   * the report and owns the slot it lives in.
   */
  onFieldOrderChanged?: (labels: string[]) => void
  onSaveError: (message: string) => void
  /**
   * The desktop keyboard cycle (brief §22's "Клавиатурный цикл"). Undefined on
   * mobile, where none of this applies — `handleCardKeyDown` below returns
   * immediately when it is, so every branch here is additive and the phone's
   * key handling (native Tab through handle → trash → field, same as before)
   * is untouched.
   *
   * Only the boundary crossings are reported upward: everything else that
   * needs a card's own draft (add/delete a field) is handled locally, the
   * same as a mouse click on the same controls would be.
   */
  onKeyboardCycle?: {
    /** ⇥ pressed while the LAST field (or Term, if there is no side-B field
     * yet) holds the caret. The caller decides what "next" means across the
     * card boundary — this component has no notion of a next card. */
    onTabPastLastField: () => void
    /** ⇧⇥ pressed while Term — always the first stop — holds the caret. */
    onShiftTabBeforeTerm: () => void
    /** ⌘⏎ — a new card below, caret in Term. */
    onNewCardBelow: () => void
    /** ⎋ — end this card's edit session. Returns whether it acted: `false`
     * means the caller had nothing per-card to end (the deck-wide editing
     * mode is what makes this card editable), and the press must be left for
     * the screen's own Escape ladder to resolve — so this handler does NOT
     * `preventDefault()` in that case. */
    onEscape: () => boolean
    /** ⌘F — search this deck. */
    onSearchInDeck: () => void
    /** ⌘K — search everywhere. Reported rather than called directly because
     * the global Cmd+K listener (`Layout.tsx`) deliberately skips input/
     * textarea elements to avoid stealing an ordinary typing shortcut
     * elsewhere in the app; the editing cycle needs it to fire FROM a field,
     * so it is answered here instead of relying on that listener. */
    onSearchEverywhere: () => void
  }
}

function ActiveVocabCardImpl({
  card, deckId, onCreated, deckLanguage, cardNumber, initialFieldKey, initialCaret,
  onToggleFieldChooser, fieldChooserOpen,
  onFieldFocus, onFieldBlur, onFocusedFieldType, missingFieldType, onFieldDeleted,
  onFieldOrderChanged, onSaveError, onKeyboardCycle,
}: Props, ref: ForwardedRef<ActiveVocabCardHandle>) {
  const { tr } = useLocale()
  const ce = tr.cardEditor
  const cb = tr.cardBubble
  const dd = tr.deckDetail

  // Stored position order, explicitly. The API already returns sides ordered by
  // position, but this array's order becomes the written positions on every
  // save, so relying on that implicitly would make a change of transport a
  // silent data reorder.
  //
  // Seeded from the copy the person is looking at, not from the `card` prop
  // alone. The prop comes from the deck list's cache, which is one round trip
  // behind any write still settling — and a full-replace payload built from a
  // draft seeded there deletes whatever that write added
  // (docs/diagnostics/deck-save-visibility-window-2026-08-11.md). A write's own
  // response is recorded in `lib/cardSnapshots` and supersedes the list when it
  // is newer, so re-entering a card the instant after saving it seeds from what
  // the server actually answered, with no round trip at all.
  //
  // `visibleCard` goes one further and prefers an unconfirmed change over both
  // (ADR-072): leaving a card and coming straight back finds the change still
  // there, because what the previous mount published IS this mount's seed.
  // There is no second copy to reconcile — this is the same draft, picked back
  // up. What the SERVER holds is a separate question and is asked separately
  // (`freshestServerCard` below), because reconciliation must never merge the
  // draft with itself.
  const [term, setTerm] = useState(() => findTermSide(visibleCard(card).sides)?.content ?? '')
  const [fields, setFields] = useState<FieldDraft[]>(
    () => draftsFromCardSides(
      [...visibleCard(card).sides].sort((a, b) => a.position - b.position),
    ),
  )
  const [focusedKey, setFocusedKey] = useState<string | null>(initialFieldKey)

  // ── What the person has touched in THIS mount ────────────────────────────
  //
  // Reconciliation (below) merges later server answers into the draft, and this
  // is the line it must not cross: where the author has changed something in
  // this mount, their version wins until they leave it. Refs rather than state
  // — nothing renders differently because a field is dirty, and a re-render per
  // first-keystroke-in-a-field would be a cost for no display.
  //
  // Seeded from the published draft when this mount is picking one up (ADR-072
  // — leaving a card and coming back does not end the change, so it must not
  // reset what counts as authored either). Without this the author's own
  // unsaved edit would arrive at reconciliation looking untouched, and the
  // server's older copy would be merged straight over it: the change would
  // survive the remount only to be undone by the next answer.
  const [seededTouch] = useState(() => (card.id ? getCardDraftTouch(card.id) : null))
  const touchedTermRef = useRef(seededTouch?.term ?? false)
  // Filled on the first render only — a `useRef` argument is evaluated on every
  // render, and this surface re-renders per keystroke.
  const touchedFieldsRef = useRef<Set<string> | null>(null)
  if (touchedFieldsRef.current === null) {
    touchedFieldsRef.current = new Set(seededTouch?.fields ?? [])
  }
  // Identities of fields dropped here — deleted, or emptied — so a server
  // answer that still carries one does not undo the decision. Keyed by the
  // field's stored id since ADR-074: ids now survive the round trip this has to
  // reason across, and the kind+content token this replaces could not recognise
  // a deletion whose text had been edited first.
  const removedFieldsRef = useRef<Set<string> | null>(null)
  if (removedFieldsRef.current === null) {
    removedFieldsRef.current = new Set(seededTouch?.removed ?? [])
  }
  function markFieldTouched(key: string) { touchedFieldsRef.current!.add(key) }
  function touchState(): DraftTouch {
    return {
      term: touchedTermRef.current,
      fields: touchedFieldsRef.current!,
      removed: removedFieldsRef.current!,
    }
  }

  // Keys of fields added THIS session via + Field that have never held
  // non-blank content. A key leaves this set the moment its field gets real
  // content (it is then an ordinary field, subject to the explicit trash-can
  // delete like any other) — so re-focusing a filled-then-emptied field does
  // not retroactively make it discardable.
  const pendingNewKeysRef = useRef<Set<string>>(new Set())
  // Set by the imperative addField handle; consumed by an effect that focuses
  // the row once React has committed it, reusing the same handleFocus path a
  // tap on an existing field takes (so §7 positioning applies identically).
  const [focusRequestKey, setFocusRequestKey] = useState<string | null>(null)
  // Which attachment field (if any) has its file widget open beneath its chip.
  // One at a time by construction: a second `Replace` replaces the first rather
  // than stacking two 180px previews on one card.
  const [replacingKey, setReplacingKey] = useState<string | null>(null)

  const updateCard = useUpdateCard()
  const createCard = useCreateCard()
  const { speak, cancelSilently, isSpeaking } = useSpeech(deckLanguage)

  // The server id, once known. A ref rather than only state because `commit`
  // runs from unmount cleanup, where a state read would be stale — and getting
  // this wrong would POST a second copy of a card that already exists.
  const savedIdRef = useRef<string>(card.id)
  const creatingRef = useRef(false)
  // Render-reactive twin of `savedIdRef`. A ref alone would be correct but
  // silent — nothing would re-render when the id arrives — and two things need
  // to react to it: Fill, which must not offer itself on a brand-new card that
  // has never been POSTed (§10), and the senses query, whose KEY is this id.
  const [savedId, setSavedId] = useState<string>(card.id)
  const hasSavedId = Boolean(savedId)
  const writeFailed = useCardWriteFailed(savedId)

  /*
   * There is deliberately no local mirror of the card's corpus linkage here
   * any more. One existed to carry a just-established link ahead of the late
   * prop, because the arrows were gated on having a link; ADR-070 §6 removes
   * that gate — the arrows are offered on the strength of the current Term
   * having candidates, never on the stored pointer — so the value it fed has
   * no reader left. It also survived a Term edit within one mount, keeping the
   * arrows alive from a linkage that no longer applied, which is the second
   * half of what §6 closes.
   */

  // Signature of what the server was last told. Commits compare against it so
  // moving through five fields without typing costs zero requests, while a
  // failure clears it so the next commit retries instead of assuming success.
  //
  // Initialised from what the SERVER holds, not from this render's draft. Those
  // used to be the same thing — the draft was seeded from a server copy, so
  // either reading described the same side list. They are no longer: a draft
  // seeded from an unconfirmed change (a write still in flight, or one that
  // failed) is by definition not what the server was told, and initialising
  // from it would have this mount believe that change was already saved and
  // never send it.
  const lastSavedRef = useRef<string | null>(null)
  // What the server is known to hold, in the same normalisation. Kept because
  // it answers a question `lastSavedRef` cannot: whether the draft is carrying
  // a change nobody has answered for, which is what decides whether it is
  // published for the list to show (ADR-072).
  const confirmedRef = useRef<string | null>(null)
  if (lastSavedRef.current === null) {
    const seedServer = card.id ? freshestCard(card) : null
    confirmedRef.current = seedServer
      ? serverSideSignature(seedServer.sides)
      : draftSignature(term, fields)
    lastSavedRef.current = confirmedRef.current
  }

  // The prop, readable from callbacks that may be a render behind it.
  const cardPropRef = useRef(card)
  cardPropRef.current = card
  // `updated_at` of the newest server answer this draft has been reconciled
  // with (the seed counts as the first). Later answers are folded in; an older
  // one that arrives out of order is ignored rather than allowed to move the
  // draft backwards.
  const serverStampRef = useRef<string | null>(null)
  if (serverStampRef.current === null) {
    serverStampRef.current = freshestCard(card).updated_at ?? ''
  }
  /** The exact server object `applyServerAnswer` last folded in — identity,
   * not the timestamp, is what distinguishes a genuinely new answer from the
   * same one being handed back in on a later call (see `applyServerAnswer`). */
  const lastAppliedServerRef = useRef<Card | null>(null)
  /** The best copy of THIS card the client holds — the list's, or a recorded
   * write response, whichever is newer. Null for a card with no server id yet. */
  function freshestServerCard(): Card | null {
    const id = savedIdRef.current
    if (!id) return null
    return cardPropRef.current.id === id
      ? freshestCard(cardPropRef.current)
      : getCardSnapshot(id)
  }

  /**
   * Fold a server answer into the draft and answer with the draft to work from
   * (see `reconcileDraftWithServer` for the merge rules).
   *
   * This is the whole of "the draft reconciles with the server instead of
   * trusting its first seed forever". It runs from three places, and all three
   * matter: when a later copy of the card arrives on the prop, when a write's
   * own response is recorded, and immediately before a write is built — the
   * last because a full-replace payload is the one thing that must never be
   * built from a picture known to be behind.
   */
  function applyServerAnswer(
    server: Card | null, base: { term: string; fields: FieldDraft[] },
  ): { term: string; fields: FieldDraft[] } {
    if (!server?.id || server.id !== savedIdRef.current) return base
    // Commit calls this with `freshestServerCard()` on every write it builds
    // for an existing card (line ~482), whether or not anything has actually
    // answered since the last time this ran — reference identity, not the
    // timestamp check below, is what tells "already folded this one in" from
    // "here is something new". Without this, re-applying the same known
    // answer still ran the "new information" branch below, republishing the
    // draft for no reason and bumping its `seq` past what the write about to
    // be issued captured — which is what left `recordCardSnapshot`'s clearing
    // guard permanently unable to see "nothing published since" even on a
    // write that landed clean (docs/diagnostics/late-write-after-reconnect-
    // and-stuck-unsaved-mark-2026-08-11.md §3).
    if (server === lastAppliedServerRef.current) return base
    const stamp = server.updated_at ?? ''
    if (stamp && serverStampRef.current && stamp < serverStampRef.current) return base
    serverStampRef.current = stamp || serverStampRef.current
    lastAppliedServerRef.current = server

    const merged = reconcileDraftWithServer(base, server.sides, touchState())

    // What the server actually holds, in the same normalisation a save uses —
    // so a draft that reconciliation brought into agreement with it writes
    // nothing, and re-entering a card without editing stays a no-op. Skipped
    // while one of our own writes is in flight: until it answers, what the
    // server will hold is the payload already sent, not this older copy.
    confirmedRef.current = serverSideSignature(server.sides)
    publishedSignatureRef.current = null
    if (!pendingCardWrite(savedIdRef.current)) {
      lastSavedRef.current = confirmedRef.current
    }

    if (!merged) return base
    setTerm(merged.term)
    setFields(merged.fields)
    // Written through immediately as well: a commit deferred behind an
    // in-flight write resumes in a microtask, well before the effect that
    // ordinarily refreshes these would run, and it must build from the
    // reconciled draft rather than the one that was behind.
    termRef.current = merged.term
    fieldsRef.current = merged.fields
    return merged
  }
  const applyServerAnswerRef = useRef(applyServerAnswer)
  applyServerAnswerRef.current = applyServerAnswer

  // A commit that is waiting for an in-flight write to answer. One at a time:
  // the resume rebuilds from the latest draft, so a second deferral would only
  // duplicate the same write.
  const deferredCommitRef = useRef(false)
  // This component's own in-flight write, held from the instant it is issued.
  // The registry in `lib/cardSnapshots` is written inside the mutation function,
  // which React Query runs a microtask later — long enough for two fast commits
  // to both see an empty registry and race each other.
  const ownWriteRef = useRef<Promise<void> | null>(null)

  function commit(rawTerm: string, rawFields: FieldDraft[]) {
    // An empty Term is not a saveable card (the backend requires every side's
    // content to be non-empty, Term included). For an existing card this
    // leaves the draft alone and lets the user finish; for a NEW card it is
    // also the whole of "an empty new card is discarded silently" (§10) —
    // nothing was ever written, so there is nothing to undo.
    if (!rawTerm.trim()) return

    let nextTerm = rawTerm
    let nextFields = rawFields
    if (savedIdRef.current) {
      // A write to this card is still in flight, so the draft does not yet know
      // what that write did — and this write REPLACES the card's whole side
      // list. Sending now is exactly how a landed write gets erased. Wait for
      // the answer, reconcile against it, and then write once.
      const inFlight = pendingCardWrite(savedIdRef.current) ?? ownWriteRef.current
      if (inFlight) {
        if (!deferredCommitRef.current) {
          deferredCommitRef.current = true
          // Resumes on failure exactly as on success: a failed write leaves the
          // server where it was, and the draft is then the only copy of the
          // author's work. The continuation is not bound to this component's
          // lifetime, so a commit deferred by "Done" still lands after unmount.
          const resume = () => {
            deferredCommitRef.current = false
            commitLatestRef.current()
          }
          inFlight.then(resume, resume)
        }
        return
      }
      const merged = applyServerAnswer(
        freshestServerCard(), { term: nextTerm, fields: nextFields },
      )
      nextTerm = merged.term
      nextFields = merged.fields
    }

    const payload = buildSidesFromDraft(nextTerm, nextFields)
    // Identity is excluded from the signature (`draftSignature`): a field that
    // has just acquired its stored id has not changed, and a signature that
    // moved when the id arrived would have this surface believe it owed a write
    // it had already made.
    const signature = draftSignature(nextTerm, nextFields)
    if (signature === lastSavedRef.current) return

    // First write of a brand-new card: POST. No `position` is sent — the
    // server appends at max(position)+1 across the whole deck, which is what
    // makes this correct on a partially loaded feed.
    if (!savedIdRef.current) {
      // A POST already in flight: leave the signature untouched so the
      // follow-up commit fired from onSuccess picks the newer text up as a
      // PATCH instead of creating a second card.
      if (creatingRef.current) return
      creatingRef.current = true
      lastSavedRef.current = signature
      // `mutateAsync` with plain promise continuations, NOT `mutate`'s
      // per-call callbacks. The commonest way to finish a new card is to leave
      // it — Tab to the next card, Escape, Done — and every one of those
      // unmounts this component while the POST is still in flight. React Query
      // drops a `mutate()` callback whose observer has gone, so `onCreated`
      // would never fire on exactly that path, taking with it both the
      // duplicate-row guard and the chosen position an insert divider asked
      // for: the card would land at the end of the deck with nothing to say
      // so. A promise continuation is not bound to the observer's lifetime and
      // runs either way. The state writes below are no-ops after unmount,
      // which is correct — there is nothing left to show.
      createCard.mutateAsync({ deckId, body: { sides: payload } })
        .then((created) => {
          creatingRef.current = false
          savedIdRef.current = created.id
          setSavedId(created.id)
          onCreated?.(created)
          // Anything typed while the POST was in flight is still unsaved —
          // flush it now that there is an id to PATCH.
          commitLatestRef.current()
        })
        .catch((err) => {
          creatingRef.current = false
          lastSavedRef.current = ''
          onSaveError(extractErrorMessage(err, ce.saveErrorGeneric))
        })
      return
    }

    lastSavedRef.current = signature
    // `mutateAsync` with a promise continuation, not `mutate` with per-call
    // callbacks, for the reason the create path above already documents: the
    // commonest way to finish editing unmounts this component while the write
    // is in flight, and React Query drops a per-call callback whose observer
    // has gone. That silently swallowed the save-error report on exactly the
    // path where it matters most. The promise is also what a deferred commit
    // waits on, held from the instant the write is issued.
    const writtenId = savedIdRef.current
    const write = updateCard.mutateAsync({ id: writtenId, body: { sides: payload } })
      .then(() => {}, (err) => {
        lastSavedRef.current = ''
        // The change is not withdrawn — not here and not anywhere (ADR-072
        // Decision 4). It stays in the draft, stays published, and is now
        // marked: a card edit is authored text, and taking it off the screen to
        // describe what happened to it would destroy the only copy that could
        // be retried. The report below says so; the mark is what remains after
        // the report has gone.
        markCardWriteFailed(writtenId)
        onSaveError(extractErrorMessage(err, ce.saveErrorGeneric))
      })
    ownWriteRef.current = write
    void write.then(() => { if (ownWriteRef.current === write) ownWriteRef.current = null })
  }

  // Latest-values commit, callable from cleanup. The ref is reassigned every
  // render so the unmount path can never flush a stale draft — this is the one
  // thing standing between "Done" and a lost keystroke. It reads the REFS
  // rather than this render's values because a deferred commit resumes from a
  // microtask that reconciliation may have run in between, and the write it
  // then builds must be the reconciled one.
  const commitLatestRef = useRef<() => void>(() => {})
  const fieldsRef = useRef(fields)
  const termRef = useRef(term)

  /**
   * Publish the draft for everything else that shows this card — the deck list
   * on both platforms (ADR-072 Decision 1).
   *
   * Published only while the draft differs from what the server is known to
   * hold, and retracted the moment it agrees again. That is what keeps this an
   * unconfirmed change rather than a rival cache of cards: a card entered and
   * left without editing publishes nothing, so nothing of it stands between a
   * later server answer and the list.
   *
   * A blank Term is not published for the same reason it is not written: it is
   * not a saveable state, and showing the list a card with no term would be the
   * display running ahead of something that is never going to land.
   */
  function publishDraft(nextTerm: string, nextFields: FieldDraft[]) {
    const id = savedIdRef.current
    if (!id || !nextTerm.trim()) return
    const signature = draftSignature(nextTerm, nextFields)
    if (signature === confirmedRef.current) {
      publishedSignatureRef.current = null
      retractCardDraft(id)
      return
    }
    // This component re-renders for plenty of reasons that are not a change to
    // the draft — focus moving, a chooser opening, a hover. Publishing on each
    // of those would hand the list a new object to render for no new content.
    if (signature === publishedSignatureRef.current) return
    publishedSignatureRef.current = signature
    const base = freshestServerCard() ?? cardPropRef.current
    // A field this draft has emptied is not in the published side list — a save
    // omits it, and the published draft is the shape a save would send. The
    // mount that picks this draft up therefore has no row to recognise the
    // server's copy by, so the decision to empty it travels with the draft as a
    // dropped identity, exactly as an explicit deletion does (ADR-074). Within
    // this mount the row is still here and matches by identity; this is only
    // for the mount that comes after.
    publishCardDraft(id, {
      ...base, id, sides: draftToCardSides(nextTerm, nextFields, base.sides),
    }, {
      ...touchState(),
      removed: new Set([
        ...removedFieldsRef.current!,
        ...droppedFieldIdentities(nextTerm, nextFields),
      ]),
    })
  }
  /** The draft content most recently published, so an unchanged draft is not
   * republished. Cleared whenever the server's copy changes, since the base the
   * publication is built on has then moved. */
  const publishedSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    termRef.current = term
    fieldsRef.current = fields
    commitLatestRef.current = () => commit(termRef.current, fieldsRef.current)
    // After every change to the draft, not only on commit: a field added, a
    // field deleted, a rename and a reorder all reach the list at the moment of
    // the gesture rather than at the moment its write is issued, and the two are
    // not the same instant.
    publishDraft(term, fields)
  })
  useEffect(() => () => commitLatestRef.current(), [])

  // ── Reconciliation: the draft keeps up with the server ────────────────────
  //
  // Two sources, and they cover the two halves of the save-visibility window:
  //
  //  • THE PROP. A later copy of the card arriving from the deck list — the
  //    refetch a write's invalidation triggered, or any other read. This is
  //    also what stops a card left open for a long time from writing an
  //    arbitrarily old picture back.
  //  • THE WRITE'S OWN ANSWER. Recorded in `lib/cardSnapshots` by whichever
  //    mount issued it, which is frequently not this one: finishing a card
  //    unmounts the surface that was editing it, and the re-entry that lands
  //    inside the window is a different mount of the same card. This arrives a
  //    full list round trip earlier than the prop does.
  //
  // Neither reseeds. `applyServerAnswer` merges, and where the person has
  // touched something in this mount, their version wins.
  useEffect(() => {
    applyServerAnswerRef.current(card, { term: termRef.current, fields: fieldsRef.current })
  }, [card])
  // The recorded answer this draft has already been shown. The store notifies
  // one set of listeners for everything it holds about a card, and since
  // ADR-072 that includes this component's own publications — so without this
  // guard a keystroke would run a reconciliation against a server answer that
  // has not changed since the last one.
  const seenSnapshotRef = useRef<Card | null>(null)
  useEffect(() => {
    if (!savedId) return
    return subscribeCardSnapshot(savedId, () => {
      const snap = getCardSnapshot(savedId)
      if (snap === seenSnapshotRef.current) return
      seenSnapshotRef.current = snap
      applyServerAnswerRef.current(
        snap, { term: termRef.current, fields: fieldsRef.current },
      )
    })
  }, [savedId])

  // ── Focus + caret placement for the tap that activated this card ──────────
  const inputsRef = useRef(new Map<string, HTMLTextAreaElement | HTMLInputElement>())
  const rowsRef = useRef(new Map<string, HTMLElement>())
  // The card's own root. Reported with every focus because Decision A positions
  // the card, not the row: its rect bounds the walk (there is nothing beyond a
  // card's edges to reach by tapping within it) and it is what makes the
  // action row part of the stack rather than chrome hanging off the end.
  const cardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!initialFieldKey) return
    const el = inputsRef.current.get(initialFieldKey)
    if (!el) return
    // `preventScroll` is load-bearing here, not a micro-optimisation. Focusing
    // an element the browser considers off screen makes Chrome scroll the
    // nearest scrollable ancestor — `<main id="app-scroll">` — to reveal it,
    // which is a second scroll writer racing our own placement. Harmless while
    // this only ever ran on a card the finger had just tapped (already on
    // screen); Previous/Next now focuses a card that may be far away, which is
    // exactly the competing-writer defect class ADR-067 Decision D forbids.
    // The placement below still brings the card into position — through the one
    // motion, and against geometry it measures itself.
    el.focus({ preventScroll: true })
    const offset = initialCaret === null
      ? el.value.length
      : Math.max(0, Math.min(initialCaret, el.value.length))
    try { el.setSelectionRange(offset, offset) } catch { /* type doesn't support it */ }
    // Mount-only: re-running on a prop change would yank the caret out of
    // whatever field the user has since moved to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // + Field (brief §11): appends and requests focus. The actual DOM focus
  // happens in the effect below, once the appended row has committed — a ref
  // callback registers the input during the SAME commit that renders it, so
  // by the time this effect runs the element is already in `inputsRef`.
  // One implementation, two callers: the `+ Field` chooser reaches it through
  // the imperative handle, and §13's `+ Add Example` affordance calls it
  // directly. Both must create the same field in the same place with the same
  // focus, which is a guarantee only shared code can make.
  function addField(kind: string, label?: string) {
    // The loader's own predicate, called rather than restated: a note-kind
    // field is custom precisely when its label is something other than the
    // plain "Note". Treating EVERY note-kind field as custom was safe only
    // while `note` reached here from one caller — the Custom pill. The chooser
    // now also offers a plain Note, and under the old rule that field would
    // have been custom-named-"Note" this session and a plain Note after a
    // reload: the same field disagreeing with itself about whether its name is
    // editable. Sharing the predicate is what makes the two agree by
    // construction.
    const named = (label ?? '').trim()
    const isCustom = isCustomNamedSide({ kind, label: named })
    const fieldLabel = kind === 'note' ? named : STANDARD_LABEL[kind]
    // A Custom field with no name is discarded here, the same way a field
    // left empty is discarded on blur (§11): it is never created, leaves no
    // trace and gets no Undo. The alternative — inventing "Custom field" —
    // would name a field something the user did not type, which is the one
    // thing the custom type exists to prevent.
    if (!fieldLabel) return
    const key = newFieldKey()
    pendingNewKeysRef.current.add(key)
    // A field created here is the author's from the moment it exists — it has
    // no server counterpart to be reconciled against, and must not acquire one
    // by sitting at an index a server side also occupies.
    markFieldTouched(key)
    // No `serverId`: the server has never answered for this field. It acquires
    // one from the answer to the write that creates it (ADR-074).
    setFields((prev) => [...prev, { key, serverId: null, kind, label: fieldLabel, content: '', isCustom }])
    setFocusRequestKey(key)
  }
  // Reassigned every render so the handle and the affordance both call the
  // latest closure, the same discipline `commitLatestRef` uses.
  const addFieldRef = useRef(addField)
  addFieldRef.current = addField
  const addImageFieldRef = useRef(addImageField)
  addImageFieldRef.current = addImageField

  /**
   * An image pasted straight onto the card (moved from the retired desktop
   * modal, ADR-067 ninth amendment).
   *
   * Built here rather than in either caller, so the phone's active card and the
   * desktop right panel get it by construction — they are this component,
   * mounted twice. `ImageSideInput` already takes a file or a typed URL; what
   * had no route on this surface is the clipboard.
   *
   * Two cases, and the asymmetry between them is deliberate and inherited:
   *
   *  • An image FILE is claimed unconditionally. No text field can accept one,
   *    so intercepting it competes with nothing, and requiring the reader to
   *    first click away from whatever they were typing in would be a rule with
   *    no purpose behind it.
   *  • An image URL is claimed only when the caret is NOT in a field. Pasting a
   *    URL into a Link or a Custom field is an ordinary paste and stays one;
   *    hijacking it would make the surface unable to store the one kind of text
   *    that looks like a picture.
   *
   * Scoped by this component's own mount, which is the same thing as "a card is
   * open for editing" — there is no separate is-this-card-active flag to fall
   * out of step with. `DeckDesktopPanels` also listens for pastes on the pane,
   * but only claims two or more LINES of text, so the two cannot both fire on
   * one paste.
   */
  useEffect(() => {
    async function handlePaste(e: ClipboardEvent) {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'))
      if (file) {
        e.preventDefault()
        try {
          // The same compression every other image on a card goes through —
          // a pasted screenshot is otherwise several megabytes of data URL in
          // a column the backend caps.
          addImageFieldRef.current(await compressImageForStorage(file))
          toast.success(cb.imagePastedToast)
        } catch {
          toast.error(cb.imagePasteFailedToast)
        }
        return
      }
      const active = document.activeElement
      const typing = active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA'
        || (active as HTMLElement | null)?.isContentEditable === true
      if (typing) return
      const text = e.clipboardData?.getData('text')?.trim()
      if (!text || !isImageContent(text)) return
      e.preventDefault()
      addImageFieldRef.current(text)
      toast.success(cb.imagePastedToast)
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [cb])

  useImperativeHandle(ref, () => ({
    addField(kind, label) { addFieldRef.current(kind, label) },
    focusField(key, caret) {
      const el = inputsRef.current.get(key)
      if (!el) return
      // `preventScroll` for the same reason the mount focus uses it: focusing an
      // element the browser thinks is off screen makes it scroll the nearest
      // scrollable ancestor, which on desktop is the right panel itself — a
      // second scroll writer racing whatever placement is in flight (ADR-067
      // Decision D). The panel's own `scrollIntoView` is the one motion.
      el.focus({ preventScroll: true })
      const offset = caret === null || caret === undefined
        ? el.value.length
        : Math.max(0, Math.min(caret, el.value.length))
      try { el.setSelectionRange(offset, offset) } catch { /* type doesn't support it */ }
    },
  }), [])

  useEffect(() => {
    if (!focusRequestKey) return
    const el = inputsRef.current.get(focusRequestKey)
    // Same reason as the mount focus above: the placement owns the page, so the
    // focus call must not move it (ADR-067 Decision D).
    el?.focus({ preventScroll: true })
    setFocusRequestKey(null)
  }, [focusRequestKey])

  function handleFocus(key: string) {
    setFocusedKey(key)
    const row = rowsRef.current.get(key)
    if (row) onFieldFocus(row, cardRef.current)
    // Brief §13 — the type of the field holding the caret is what Previous/Next
    // carries to the next card. Reported from the live draft so a field created
    // moments ago by `+ Field`, which has no stored side yet, is as walkable as
    // any other.
    if (onFocusedFieldType) {
      const draft = key === TERM_FIELD_KEY ? null : fieldsRef.current.find((f) => f.key === key)
      onFocusedFieldType(draft ? fieldTypeOfDraft(draft) : TERM_FIELD_TYPE)
    }
  }

  function handleBlur(key: string) {
    // Commit before anything else: leaving a field is the commit point, whether
    // the next stop is another field, another card, or Done.
    commit(termRef.current, fieldsRef.current)
    // A field added by + Field that is still empty the moment focus leaves it
    // never existed as far as the card is concerned (brief §11) — discarded
    // silently, no Undo, unlike an explicit trash-can delete (§12).
    if (pendingNewKeysRef.current.has(key)) {
      pendingNewKeysRef.current.delete(key)
      const current = fieldsRef.current.find((f) => f.key === key)
      if (current && !current.content.trim()) {
        setFields((prev) => prev.filter((f) => f.key !== key))
      }
    }
    setFocusedKey((prev) => (prev === key ? null : prev))
    onFieldBlur()
  }

  // ── Edits ────────────────────────────────────────────────────────────────
  function setFieldContent(key: string, content: string) {
    // From here on this field is the author's: reconciliation may not rewrite it
    // from a server answer for as long as this mount lives.
    markFieldTouched(key)
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, content } : f)))
  }

  /**
   * An image or audio field whose file was just replaced.
   *
   * Separate from `setFieldContent` because of the COMMIT, not the write: every
   * other field commits when the caret leaves it, and an attachment has no
   * caret to leave — it is changed by a file picker, a URL box or a recorder,
   * none of which blur anything this component is watching. Left to the ordinary
   * rule, a replaced image would sit in the draft until some unrelated field was
   * focused and left, which is a save the author has no way to predict.
   *
   * `next` is passed to `commit` explicitly rather than read back from state,
   * for the same reason `deleteField` does it: the state set on the line above
   * is not readable synchronously.
   */
  function addImageField(content: string) {
    // Deliberately NOT `addField` + `setFieldContent`. `addField` creates an
    // EMPTY field, registers it in `pendingNewKeysRef` and focuses it — all
    // three wrong here. This field arrives with its content, is never focused,
    // and must therefore never be on the discard-if-empty-on-blur list, which
    // it would leave only by being focused and left.
    const key = newFieldKey()
    markFieldTouched(key)
    const next: FieldDraft[] = [
      ...fieldsRef.current,
      { key, serverId: null, kind: 'image', label: STANDARD_LABEL.image, content, isCustom: false },
    ]
    setFields(next)
    // Committed at once, with the draft passed explicitly — the attachment
    // rule below, and for the same reason: there is no caret here to leave.
    // On a brand-new card whose Term is still blank `commit` returns early and
    // the field waits in the draft, which is the ordinary rule for every field
    // on such a card, not a special case.
    commit(termRef.current, next)
  }

  function setAttachmentContent(key: string, content: string) {
    markFieldTouched(key)
    const next = fieldsRef.current.map((f) => (f.key === key ? { ...f, content } : f))
    setFields(next)
    commit(termRef.current, next)
    // The widget's job is done the moment it produced a file; leaving it open
    // would leave a 180px preview standing over the rest of the card.
    setReplacingKey(null)
  }

  // A custom field's name is its label, always — no substitution, so the row
  // shows exactly what was typed into the chooser. Every other kind shows the
  // translated kind name (its stored label is fixed English).
  function labelFor(f: FieldDraft): string {
    if (f.isCustom) return f.label.trim()
    return DISPLAY_LABEL(ce)[f.kind] ?? f.label
  }

  // ── Renaming a custom field (moved from the full-screen editor) ───────────
  //
  // `FieldTypeChooser` asks for a custom field's name once, before its content
  // (brief §11), and never again — so before this, a typo in a field name was
  // permanent. The name is half of what its author writes, so it stays editable
  // where it is displayed.
  //
  // The hazard this has to guard, which the editor did not: `buildSidesFromDraft`
  // DISCARDS a custom field whose label is blank — deliberately, since a
  // nameless custom field is as incomplete as an empty one. Clearing the name in
  // order to retype it would therefore, at the next commit, discard the field
  // AND its content. Under the editor's 500ms debounced autosave that was a
  // window on every keystroke; here the commit is on blur, and a blank name on
  // blur is restored rather than saved. Emptying a name is not a way to delete
  // a field — the trash can already is one, and it offers an Undo.
  const labelBeforeRenameRef = useRef<{ key: string; label: string } | null>(null)

  function setFieldLabel(key: string, label: string) {
    markFieldTouched(key)
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, label } : f)))
  }

  function commitFieldLabel(key: string) {
    const before = labelBeforeRenameRef.current
    labelBeforeRenameRef.current = null
    const previous = before && before.key === key ? before.label : ''
    const next = restoreBlankCustomLabel(fieldsRef.current, key, previous)
    // Identity means nothing was restored — either the name is fine, or it is
    // blank with nothing to restore it to (a state `addField` refuses to
    // create). The second case must not commit: committing a blank custom label
    // is exactly the discard this guard exists to prevent.
    if (next !== fieldsRef.current) {
      setFields(next)
    } else if (!fieldsRef.current.find((f) => f.key === key)?.label.trim()) {
      return
    }
    commit(termRef.current, next)
  }

  function deleteField(f: FieldDraft) {
    const index = fields.findIndex((x) => x.key === f.key)
    const next = fields.filter((x) => x.key !== f.key)
    setFields(next)
    pendingNewKeysRef.current.delete(f.key)
    // Remembered so a server answer still carrying this field — one written
    // before the deletion, or the list's copy arriving late — cannot put it
    // back. A deletion is a decision, and reconciliation does not overturn it.
    // By identity, so it is recognised as the same field however much its text
    // was changed before it was deleted (ADR-074). A field the server has never
    // answered for has nothing to suppress: no answer can carry it.
    if (f.serverId) removedFieldsRef.current!.add(f.serverId)
    touchedFieldsRef.current!.delete(f.key)
    if (replacingKey === f.key) setReplacingKey(null)
    if (focusedKey === f.key) { setFocusedKey(null); onFieldBlur() }
    commit(term, next)
    // An empty field leaves nothing to lose, so it goes immediately and
    // silently; a filled one is recoverable for as long as the affordance
    // lives. No modal either way (§12).
    if (f.content.trim()) {
      onFieldDeleted({
        label: labelFor(f),
        restore: () => {
          const restored = [...fieldsRef.current]
          restored.splice(Math.min(index, restored.length), 0, f)
          // The deletion is withdrawn, so the note that suppressed this field
          // goes with it; the restored field is the author's, as any other
          // field they put there.
          if (f.serverId) removedFieldsRef.current!.delete(f.serverId)
          markFieldTouched(f.key)
          setFields(restored)
          commit(termRef.current, restored)
        },
      })
    }
  }

  // ── Reordering fields (brief §14, ADR-066 Decision 4) ────────────────────
  //
  // Three properties of this drag are decisions, not implementation detail:
  //
  //  1. IT DOES NOT SCROLL. Not at the viewport edge, not anywhere. ADR-067
  //     Decision D allows exactly one motion per gesture and forbids a second
  //     writer, and an edge auto-scroll during a drag is that second writer in
  //     its most familiar form — it would run against a placement that may
  //     still be in flight, and against the author's own finger. So the drop
  //     target simply clamps at the ends of the field stack
  //     (`stepTowardPointer`), the page stays exactly where the author left it,
  //     and dragging past the top or bottom of the visible area moves the field
  //     as far as it can go and no further. `touch-action: none` on the handle
  //     is the same decision seen from the browser's side: the gesture is a
  //     reorder, so it is never also a scroll.
  //
  //  2. IT DOES NOT TAKE FOCUS. `preventDefault` on pointerdown (and on
  //     mousedown, for the compatibility path) means the handle never becomes
  //     the focused element, so the field the author was typing in keeps the
  //     caret and the on-screen keyboard stays up. That is the state the author
  //     reorders from, and a handle that dismissed the keyboard to be pressed
  //     would collapse the free space and re-lay-out the card mid-gesture.
  //
  //  3. IT IS CANCELLABLE, AND IT WRITES ONCE. Nothing is committed while the
  //     drag runs — the draft is reordered live so the author sees the result,
  //     but the save happens on drop and only if the order actually changed.
  //     Escape and `pointercancel` restore the order the drag started from, and
  //     since no write has happened by then there is nothing to undo. Dropping
  //     a field back where it started is the same no-op by the same rule.
  //
  // Keyboard: the handle is a real button, so it is tabbable, and Arrow
  // Up/Down move the field one place. Same path, same commit, same report.
  const dragRef = useRef<{
    key: string
    pointerId: number
    from: FieldDraft[]
    order: FieldDraft[]
    /** How far down the row the handle was pressed. The drop rule needs it to
     * judge the row by where the author is carrying it rather than by where the
     * layout has it — see `stepTowardPointer`. */
    grabOffset: number
  } | null>(null)
  const [draggingKey, setDraggingKey] = useState<string | null>(null)

  // ── Grabbed-state feedback (DESIGN_SYSTEM.md "Field drag") ────────────────
  //
  // Two independent motions, both appearance-only — neither writes a row's
  // border-box size, so neither can trip DeckMobileFeed's ResizeObserver or
  // compete with its single-motion placement (ADR-067 Decision D):
  //
  //  - LIFT: an accent-tinted halo, absolutely positioned and out of flow (see
  //    the render below), fades in behind a row the instant `draggingKey`
  //    names it and fades out the instant it stops naming it. Ordinary
  //    declarative CSS, driven by React's own style prop.
  //  - SLIDE: when a reorder actually displaces OTHER rows, each one is
  //    animated from its old position to its new one (FLIP) instead of
  //    snapping. This has to be imperative — `transform` is written to the DOM
  //    directly in the effect below, never through React's style prop —
  //    because a discrete DOM-order change is not itself a value CSS can
  //    transition; something has to measure the before/after and drive the
  //    tween by hand.
  //
  // `flipArmedRef` is the switch between "this fields-change is a reorder"
  // and "this one is a content edit, an insertion, or a deletion" (none of
  // which get the slide treatment). Every call site that reorders `fields` —
  // the live drag, cancel-restore, and `commitOrder` below — arms it
  // immediately before `setFields`.
  const flipArmedRef = useRef(false)
  function armFlip() { flipArmedRef.current = true }
  // Each row's top, as of the last time the effect below ran — this render's
  // "after" becomes the next reorder's "before". Refreshed on EVERY run, armed
  // or not, so a run of ordinary content edits (which shift rows below a
  // growing field exactly as a resize would) can never leave a stale baseline
  // for the reorder that eventually follows one.
  const flipPrevTopsRef = useRef<Map<string, number>>(new Map())
  // Rows with a slide currently running, and the timer that ends each. Tracked
  // rather than inferred, because a running slide is invisible to
  // `el.style.transform` — that is set to '' the instant the transition starts,
  // so for its whole duration the offset lives only in the computed style.
  // Membership here is what tells `measureRow` a `getComputedStyle` read is
  // owed; an empty set (the common case, including every drag under
  // reduced motion) keeps that cost off the pointermove path entirely.
  const flipAnimatingRef = useRef<Set<string>>(new Set())
  const flipTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  // Cancels a still-pending "release the keyboard flash" timer when a second
  // Arrow-key move lands before the first one's hold has elapsed, so two quick
  // presses read as one continuous lift rather than a flicker between them.
  const keyboardLiftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (keyboardLiftTimerRef.current) clearTimeout(keyboardLiftTimerRef.current)
    for (const timer of flipTimersRef.current.values()) clearTimeout(timer)
  }, [])

  /**
   * One row as the DOM currently reports it, plus the slide offset currently
   * baked into that report. Callers pass the result through `settledSpans` to
   * get the layout geometry the drop rule is defined against — see that
   * function for why measuring the painted position instead makes a stationary
   * finger oscillate.
   */
  function measureRow(key: string): MeasuredRow {
    const el = rowsRef.current.get(key)
    if (!el) return null
    const r = el.getBoundingClientRect()
    const translateY = flipAnimatingRef.current.has(key)
      ? translateYOf(window.getComputedStyle(el).transform)
      : 0
    return { top: r.top, bottom: r.bottom, translateY }
  }

  /**
   * The geometry the drop rule decides against, for one candidate order:
   * settled (no in-flight slide offsets) and tiled (no gaps between slots).
   *
   * Both the drag's start and each of its samples go through here, and that is
   * the point rather than tidiness — `grabOffset` is captured against these
   * spans and every later comparison is made against them, so a difference
   * between the two derivations would be a constant bias applied to the whole
   * gesture. That is exactly the shape of the defect this replaced.
   */
  function dropSpansFor(order: readonly FieldDraft[]) {
    return tileSpans(settledSpans(order.map((x) => measureRow(x.key))))
  }

  /** Save a new order and, if it changed a repeated kind's authority, say so. */
  function commitOrder(before: FieldDraft[], after: FieldDraft[]) {
    if (after.length === before.length && after.every((f, i) => f.key === before[i].key)) return
    armFlip()
    setFields(after)
    commit(termRef.current, after)
    if (!onFieldOrderChanged) return
    const labels = authorityChanges(before, after)
      .map((kind) => {
        const head = after.find((f) => f.kind === kind)
        return head ? labelFor(head) : null
      })
      .filter((l): l is string => Boolean(l))
    if (labels.length) onFieldOrderChanged(labels)
  }

  function handleDragStart(e: ReactPointerEvent<HTMLButtonElement>, f: FieldDraft) {
    // Keeps the caret (and the keyboard) where they are — see note 2 above.
    e.preventDefault()
    e.stopPropagation()
    const start = fieldsRef.current
    // Measured through the same pipeline every later sample uses: settled, not
    // painted (a drag can begin while a previous slide is still running, and a
    // `grabOffset` taken from a mid-slide rect would be wrong by the remaining
    // animation offset for the whole gesture), and tiled, not border-boxed (an
    // offset taken from the row's own top while the rule compares against slot
    // spans is a half-gap of constant bias — see `tileSpans`).
    const rowTop = dropSpansFor(start)[start.findIndex((x) => x.key === f.key)]?.top ?? e.clientY
    dragRef.current = {
      key: f.key, pointerId: e.pointerId, from: start, order: start,
      grabOffset: e.clientY - rowTop,
    }
    setDraggingKey(f.key)
    // Capture so the drag survives the pointer leaving the 15 px handle, which
    // it does immediately on any real gesture.
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* not supported */ }
  }

  function handleDragMove(e: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current
    if (!drag || e.pointerId !== drag.pointerId) return
    e.preventDefault()
    const current = drag.order
    const index = current.findIndex((f) => f.key === drag.key)
    if (index < 0) return
    // Re-measured every sample: the rows have just moved if the previous sample
    // swapped, and a cached measurement would decide the next step against a
    // layout that no longer exists. `dropSpansFor` is what turns those
    // measurements into the geometry the rule is defined against — settled
    // rather than painted (deciding against a running slide made a stationary
    // finger oscillate), and tiled rather than border-boxed (deciding across
    // the 11px flex gap made the gesture one-way).
    const rows = dropSpansFor(current)
    const next = stepTowardPointer(rows, index, e.clientY, drag.grabOffset)
    if (next === index) return
    const reordered = moveField(current, index, next)
    // The synchronous record of where the drag currently stands. `fieldsRef` is
    // written from an effect and is therefore a render behind; the drop must
    // save what the last pointer sample produced, not what the last commit saw.
    drag.order = reordered
    armFlip()
    setFields(reordered)
  }

  function handleDragEnd(e: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current
    if (!drag || e.pointerId !== drag.pointerId) return
    dragRef.current = null
    setDraggingKey(null)
    commitOrder(drag.from, drag.order)
  }

  function cancelDrag() {
    const drag = dragRef.current
    if (!drag) return
    dragRef.current = null
    setDraggingKey(null)
    // Nothing was written during the drag, so restoring the draft is the whole
    // of the cancellation — no request to reverse, no Undo to offer. The
    // snap-back is still a reorder as far as the slide is concerned: the
    // fields that made way for the dragged one need to slide back too, not
    // jump.
    armFlip()
    setFields(drag.from)
  }
  const cancelDragRef = useRef(cancelDrag)
  cancelDragRef.current = cancelDrag

  useEffect(() => {
    if (!draggingKey) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return
      ev.preventDefault()
      cancelDragRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [draggingKey])

  // SLIDE — see the block comment above. Runs after every render where
  // `fields` changed reference, which is every content edit as well as every
  // reorder; `flipArmedRef` is what tells the two apart, so only a reorder
  // gets the imperative transform below and a keystroke never does.
  useLayoutEffect(() => {
    // Measured before anything below overwrites a transform, and split into
    // the two facts the rest of this effect needs kept apart: where each row is
    // being PAINTED right now (`m.top`, which for a row still sliding is not
    // its layout position), and where it actually SITS (`m.top - translateY`).
    // Conflating them is the same defect `settledSpans` documents, seen from
    // the animation's side: measuring painted tops made the stored baseline
    // wrong for any reorder that landed while the previous one was still
    // running.
    const measured = new Map<string, NonNullable<MeasuredRow>>()
    for (const key of rowsRef.current.keys()) {
      const m = measureRow(key)
      if (m) measured.set(key, m)
    }
    const nextTops = new Map<string, number>()
    for (const [key, m] of measured) nextTops.set(key, m.top - m.translateY)

    if (flipArmedRef.current) {
      flipArmedRef.current = false
      const reduceMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (!reduceMotion) {
        // Where each row was VISUALLY an instant ago: its previous layout top
        // plus whatever slide offset it still carries. For a row that was at
        // rest this is just its previous layout top — the ordinary case, and
        // identical to before. For one caught mid-slide it is where the eye
        // last saw it, so an interrupted slide continues from there instead of
        // snapping back to a position it had already left.
        const fromTops = new Map<string, number>()
        for (const [key, prevTop] of flipPrevTopsRef.current) {
          fromTops.set(key, prevTop + (measured.get(key)?.translateY ?? 0))
        }
        const deltas = computeFlipDeltas(fromTops, nextTops)
        for (const [key, delta] of deltas) {
          const el = rowsRef.current.get(key)
          if (!el) continue
          // A slide already running on this row is superseded, not stacked:
          // its release timer would otherwise fire mid-way through the new one
          // and cut it short.
          const running = flipTimersRef.current.get(key)
          if (running) clearTimeout(running)
          flipAnimatingRef.current.add(key)
          el.style.transition = 'none'
          el.style.transform = `translateY(${delta}px)`
          // Forces the browser to commit the instant position above before the
          // next write starts a transition — without a forced reflow between
          // them the two writes can land in the same frame and the transition
          // never visibly starts (the standard FLIP technique's reflow step).
          void el.offsetHeight
          el.style.transition = `transform ${FIELD_DRAG_SLIDE_MS}ms ${FIELD_DRAG_EASING}`
          el.style.transform = ''
          flipTimersRef.current.set(key, setTimeout(() => {
            flipTimersRef.current.delete(key)
            flipAnimatingRef.current.delete(key)
            el.style.transition = ''
            el.style.transform = ''
          }, FIELD_DRAG_SLIDE_MS + 50))
        }
      }
    }
    // This render's settled geometry is the next reorder's baseline, armed or
    // not.
    flipPrevTopsRef.current = nextTops
  }, [fields])

  function handleHandleKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>, f: FieldDraft) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    const current = fieldsRef.current
    const index = current.findIndex((x) => x.key === f.key)
    const target = index + (e.key === 'ArrowUp' ? -1 : 1)
    if (index < 0 || target < 0 || target >= current.length) return
    // Same lift the pointer drag shows, briefly (brief's keyboard-equivalent
    // requirement) — a keypress is instantaneous and has no gesture to hold a
    // "grabbed" state for, so this is a confirmation flash rather than
    // something that tracks a finger: on for one held beat, then released,
    // the same as a drop.
    if (keyboardLiftTimerRef.current) clearTimeout(keyboardLiftTimerRef.current)
    setDraggingKey(f.key)
    commitOrder(current, moveField(current, index, target))
    keyboardLiftTimerRef.current = setTimeout(() => {
      keyboardLiftTimerRef.current = null
      setDraggingKey(null)
    }, FIELD_DRAG_LIFT_MS + KEYBOARD_LIFT_HOLD_MS)
  }

  /**
   * The desktop keyboard cycle's one entry point (brief §22), bound to the
   * card's own root so every field's keydown bubbles into it rather than
   * needing its own listener. Absent `onKeyboardCycle` (mobile), this is a
   * no-op — the phone's Tab order is native and unmodified.
   *
   * ⇥/⇧⇥ are the only two keys that must know WHICH field currently holds the
   * caret, and `focusedKey` (kept current by `handleFocus`/`handleBlur`) is
   * exactly that. Every other combination here acts on the card as a whole
   * and does not need it.
   */
  function handleCardKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (!onKeyboardCycle) return
    const mod = e.metaKey || e.ctrlKey
    if (e.key === 'Escape') {
      // Claimed only if the caller actually acted. Swallowing a press it did
      // nothing with would leave the reader with no working Escape at all —
      // see `onEscape`'s own doc above.
      if (onKeyboardCycle.onEscape()) e.preventDefault()
      return
    }
    if (mod && !e.shiftKey && !e.altKey && e.key === 'Enter') {
      e.preventDefault()
      onKeyboardCycle.onNewCardBelow()
      return
    }
    if (mod && e.shiftKey && e.key.toLowerCase() === 'f') {
      e.preventDefault()
      onToggleFieldChooser()
      return
    }
    if (mod && !e.shiftKey && e.key.toLowerCase() === 'f') {
      e.preventDefault()
      onKeyboardCycle.onSearchInDeck()
      return
    }
    if (mod && e.key.toLowerCase() === 'k') {
      e.preventDefault()
      onKeyboardCycle.onSearchEverywhere()
      return
    }
    if (e.altKey && e.key === 'Backspace') {
      // Term cannot be deleted (§12) — the same rule the trash can enforces
      // by simply not being rendered on that row.
      if (!focusedKey || focusedKey === TERM_FIELD_KEY) return
      const f = fields.find((x) => x.key === focusedKey)
      if (!f) return
      e.preventDefault()
      deleteField(f)
      // The mouse path leaves focus on the trash BUTTON, which survives its
      // own click — ⌥⌫ has no such button, it deletes the very field that
      // held focus, and that field's DOM node is what `deleteField` just
      // removed. Left alone, focus falls to the page body the instant this
      // handler returns (a focused node's removal always does that), which
      // is exactly the "stranded, need a mouse to recover" state brief §22
      // rules out. Term is the one field a delete can never remove, so it is
      // the one safe, always-present place to land — already mounted, so
      // this does not need to wait for the deletion's own re-render.
      inputsRef.current.get(TERM_FIELD_KEY)?.focus()
      return
    }
    if (e.key !== 'Tab') return
    // §5.3's controls — the handle and the trash — are `tabIndex={-1}` below,
    // so the browser's own Tab order already skips them: the only boundary
    // this needs to detect is the ends of the FIELD stack itself.
    const lastFieldKey = fields.length ? fields[fields.length - 1].key : TERM_FIELD_KEY
    if (!e.shiftKey && focusedKey === lastFieldKey) {
      e.preventDefault()
      onKeyboardCycle.onTabPastLastField()
    } else if (e.shiftKey && focusedKey === TERM_FIELD_KEY) {
      e.preventDefault()
      onKeyboardCycle.onShiftTabBeforeTerm()
    }
  }

  function handleSpeak() {
    if (isSpeaking) { cancelSilently(); return }
    speak(term)
  }

  // ADR-064 eligibility read off the DRAFT, not the loaded card, so it
  // updates as the translation is typed rather than after a round trip —
  // "without leaving the screen" taken literally. Either field suffices, and
  // that admission rule is untouched here. The mark below is a separate,
  // stricter ladder that keeps naming whichever of the pair is still missing
  // until both are present, even once the card is already eligible on one
  // field alone.
  const draftGaps: LearnGapField[] = (['translation', 'definition'] as const).filter(
    (kind) => !fields.some((f) => f.kind === kind && f.content.trim().length > 0),
  )
  const eligible = draftGaps.length < 2
  const gapKind = notInStudyKind(draftGaps)

  // Fill (ADR-067 Decision B: moved here from the read-only expanded card's
  // exclusive territory — see the class doc). Same trigger condition as the
  // not-in-study mark above, deliberately: both read the identical `eligible`
  // draft state, because Fill is one way of clearing the exact condition the
  // mark names, not an unrelated action that happens to sit near it. Gated on
  // `hasSavedId` too — a brand-new card has nothing on the server for Fill to
  // enrich yet.
  const fillMutation = useFillCard()
  const [fillError, setFillError] = useState<string | null>(null)
  const showFillTrigger = !eligible && hasSavedId
  function handleFill() {
    if (fillMutation.isPending || !savedIdRef.current) return
    setFillError(null)
    fillMutation.mutate(savedIdRef.current, {
      onSuccess: (data) => {
        if (data.ai_budget_exhausted) setFillError(dd.autofillErrorRateLimit)
        // Fill records the entry it resolved from when the card had none.
        // Nothing is mirrored locally for it: the arrows are already showing
        // (they follow the Term, not the link — ADR-070 §6), and `useFillCard`
        // invalidates this card's sense list, so the new selection arrives on
        // the refetch it triggers.
        // Fill only ever writes fields that were missing (ADR-063) — merge
        // rather than replace, so it can never clobber content the user is
        // mid-typing into a field Fill did not touch.
        // Fill's answer is a server write of this card, so the draft is now in
        // step with it: the stamp moves forward and the fields it wrote count
        // as settled rather than as something a later answer may rewrite.
        serverStampRef.current = data.card.updated_at ?? serverStampRef.current
        setFields((prev) => {
          const merged = mergeFilledSides(prev, data.card.sides)
          for (const f of merged) markFieldTouched(f.key)
          // The signature this draft now matches on the server, without a
          // round trip through `commit` — Fill wrote it directly via its own
          // endpoint, so the next blur's commit must not treat it as unsaved
          // and PATCH a redundant copy.
          //
          // `confirmedRef` moves with it, and must: Fill's answer IS a server
          // answer for this card, so the draft is in step with the server and
          // has no unconfirmed change to publish. Moving only `lastSavedRef`
          // would leave a published draft standing for a card nobody is
          // editing, with no write coming whose answer could ever supersede it
          // (ADR-072 — what is published is unconfirmed authoring and nothing
          // else). Fill itself is an excluded path and still waits for its
          // response: what arrives here is the answer, not a prediction of it.
          lastSavedRef.current = draftSignature(termRef.current, merged)
          confirmedRef.current = lastSavedRef.current
          return merged
        })
      },
      onError: (err) => setFillError(extractErrorMessage(err, cb.fillErrorGeneric)),
    })
  }

  // ── The meaning chooser (DESIGN_SYSTEM.md §14; ADR-063 Decision 3) ──────
  //
  // Moved here from the full-screen editor, which was its only home and is
  // being retired; without the move, switching a card's sense would leave the
  // product entirely (item-9 inventory §3.3). Same two endpoints, same meaning:
  // linking updates the card's corpus LINKAGE, and only ever inserts content
  // into a field that is genuinely still empty. A field the author has typed
  // into is never silently rewritten — the linkage governs what Fill and the
  // reference surfaces resolve from here on, not what is already written.
  //
  // Gated on the card having a server id, and on nothing else. ADR-070 §6:
  // whether the chooser is offered follows from the current Term having
  // candidates, never from the stored link — the endpoint computes candidates
  // from the Term the card carries now, so it is the one that can answer.
  // `savedId` (live state, not the prop) is still required: both surfaces can
  // hold an unsaved new card (`id: ''`, brief §10), which has nothing for
  // `/cards/{id}/senses` to answer about and whose query key would be built
  // from an empty id.
  const sensesEnabled = Boolean(savedId)
  const senses = useCardSenses(savedId, sensesEnabled)
  const switchSense = useSwitchCardSense()
  const senseCandidates = senses.data?.candidates ?? []

  /** The link a choice has asked for while the PATCH is in flight: the capsule
   *  and the list's mark show the reader's choice on the frame they made it,
   *  and fall back to the server's answer when the request settles either way
   *  (on success `setQueryData` has already written that answer). */
  const [pendingLinkId, setPendingLinkId] = useState<string | undefined>(undefined)
  const linkedId = pendingLinkId ?? senses.data?.selected_word_entry_id ?? null
  const linkedSense = senseCandidates.find((c) => c.word_entry_id === linkedId) ?? null
  // The refusal (ADR-063 amendment, 2026-09-11), read off the live draft so the
  // capsule's lock appears and lifts as the definition is written or cleared.
  const senseLocked = hasAuthoredDefinition(fields)

  function linkSense(candidate: CardSenseCandidate) {
    if (switchSense.isPending || !savedIdRef.current) return
    // The chooser DECLINES on a card whose definition is written (ADR-063
    // Decision 3, narrowed 2026-09-11). Re-linking used to proceed here: the
    // linkage moved, `fillEmptyFieldsFromSense` correctly refused to overwrite
    // the text (Decision 1 — user content is inviolable), and the card went on
    // resolving its translation and its form label (ADR-085) from a sense its
    // own text does not match, invisibly. The chooser already refuses in its
    // own UI; this is the same test against the ref, for a press that raced a
    // keystroke still settling in the Definition field.
    if (hasAuthoredDefinition(fieldsRef.current)) return
    setPendingLinkId(candidate.word_entry_id)
    switchSense.mutate(
      { cardId: savedIdRef.current, wordEntryId: candidate.word_entry_id },
      { onSettled: () => setPendingLinkId(undefined) },
    )
    // Read from the ref, not from `fields`: this runs from a click handler that
    // may be one render behind a keystroke still settling in a neighbouring
    // field, and the payload written below must contain that keystroke.
    // Identity is the "nothing to save" signal — see the rule's own doc.
    const before = fieldsRef.current
    const next = fillEmptyFieldsFromSense(before, candidate)
    if (next === before) return
    // The sense's content is now this card's content, authored by the gesture
    // that chose the meaning — protected from reconciliation like anything
    // else the person put here.
    for (let i = 0; i < next.length; i += 1) {
      if (next[i] !== before[i]) markFieldTouched(next[i].key)
    }
    setFields(next)
    // Committed here rather than left to the next blur: choosing a meaning is a
    // press in an overlay, and there may be no blur to carry the write. `next`
    // is passed explicitly for the reason `deleteField` does the same — state
    // is not readable back synchronously.
    commit(termRef.current, next)
  }

  // Read once per render for the lift halo's own transition (below) — the
  // SLIDE effect above makes its own, freshly-timed check instead of reading
  // this one, since it runs on a different cadence.
  const reduceMotion = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // ADR-104 Decision 6, off the LIVE draft: the field Learn studies for each
  // governed kind is its first NON-BLANK field, so a blank field is never
  // marked STUDIED and a later field the user wrote is. The meaning chooser and
  // the translation provenance anchor on that field, or on the first field of
  // the kind when the card has no value for it.
  const valueKeys = new Set(
    (['translation', 'definition', 'example'] as const)
      .map((k) => valueSide(fields, k)?.key)
      .filter((key): key is string => !!key),
  )
  const anchorKey = (kind: 'definition' | 'translation') =>
    (valueSide(fields, kind) ?? fields.find((x) => x.kind === kind))?.key
  const definitionAnchorKey = anchorKey('definition')
  const translationAnchorKey = anchorKey('translation')
  const isGovernedKind = (kind: string) =>
    kind === 'translation' || kind === 'definition' || kind === 'example'

  return (
    <div
      ref={cardRef}
      data-vocab-card=""
      onKeyDown={handleCardKeyDown}
      style={{
        display: 'flex', flexDirection: 'column', gap: 11,
        background: 'var(--bg-surface)',
        // §5.3: 2 px accent border and a floating shadow, no fill and no
        // scaling. Horizontal padding drops by the extra border width so the
        // active card occupies the same box as the collapsed one it replaced
        // and its text does not shift sideways on activation.
        border: '2px solid var(--accent)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-float)',
        padding: '11px 13px 13px',
      }}
    >
      {/* Bare number — the total belongs to the bar (§5.3). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...eyebrowStyle, color: 'var(--accent)' }}>{cb.cardNumber(cardNumber)}</span>
        {/* Only ever after a failed write (ADR-072 Decisions 3 and 4). The card
            below it is untouched — the change is still in the draft, still on
            screen, and still the only copy of it. */}
        {writeFailed && <UnsavedMark />}
      </div>

      {/* ── Term ── The one row whose label band has an empty right half: Term
          is never deletable (§12) and never reorderable, and the emptiness is
          the statement. Its speaker button lives inside the field frame. The
          same statement is made on the left: the handle gutter is RESERVED and
          left empty rather than removed, so Term's label sits on the same
          vertical line as every side-B label and the missing handle reads as an
          absence in a place that exists, not as a differently-built row (§5.3). */}
      <div
        ref={(el) => { if (el) rowsRef.current.set(TERM_FIELD_KEY, el); else rowsRef.current.delete(TERM_FIELD_KEY) }}
      >
        <div style={labelRowStyle}>
          <span aria-hidden="true" style={handleGutterStyle} />
          <span style={eyebrowStyle}>{ce.termLabel}</span>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            ref={(el) => { if (el) inputsRef.current.set(TERM_FIELD_KEY, el); else inputsRef.current.delete(TERM_FIELD_KEY) }}
            value={term}
            // Typing here makes the Term the author's for the rest of this
            // mount — reconciliation will not replace it with the server's.
            onChange={(e) => { touchedTermRef.current = true; setTerm(e.target.value) }}
            onFocus={() => handleFocus(TERM_FIELD_KEY)}
            onBlur={() => handleBlur(TERM_FIELD_KEY)}
            placeholder={ce.termPlaceholder}
            aria-label={ce.termLabel}
            style={{
              ...fieldBoxStyle(focusedKey === TERM_FIELD_KEY),
              fontWeight: 600, fontSize: 19, paddingRight: 42,
            }}
          />
          <button
            onClick={handleSpeak}
            aria-label={tr.learn.pronounceWord}
            // Same reasoning as the handle/trash on side-B rows: reachable by
            // keyboard (Shift+Tab-free navigation, screen readers), but not a
            // station between Term and the next field — the desktop cycle's ⇥
            // would otherwise cost an extra press on every card that has one
            // (brief §22).
            tabIndex={-1}
            style={{
              position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
              width: 28, height: 28, borderRadius: 'var(--radius-pill)', padding: 0,
              background: 'var(--accent-soft)', color: 'var(--accent)', border: 'none',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <Mic size={13} strokeWidth={2} />
          </button>
        </div>
      </div>

      {gapKind !== null && (
        <div style={{ alignSelf: 'flex-start' }}>
          <NotInStudyMark gapKind={gapKind} />
        </div>
      )}

      {/* ── Side B ── */}
      {fields.map((f, i) => {
        const focused = focusedKey === f.key
        const label = labelFor(f)
        const attachment = f.kind === 'image' || f.kind === 'audio'
        // ADR-066 in the label band. §5.3 lays the band out as `handle · label ·
        // BADGE · spacer · trash` and says the label truncates before the badge,
        // so the badge was specified here and simply had not been built; §22
        // then requires it on the desktop right panel, which is this same
        // component. Counted off the LIVE draft rather than the stored card, so
        // dragging a second Example above the first re-badges both in the same
        // frame the drag lands — the authority handover ADR-066 Decision 1 is
        // about, shown at the moment it happens rather than after a refetch.
        const kindRepeats = fields.some((x, j) => j !== i && x.kind === f.kind)
        const ordinalInKind = fields.slice(0, i + 1).filter((x) => x.kind === f.kind).length
        const authoritative = isGovernedKind(f.kind) ? valueKeys.has(f.key) : ordinalInKind === 1
        // Quieter, not damaged: a repeated field that Learn does not prompt from
        // says so in words rather than being hidden or reordered away.
        const subordinate = kindRepeats && !authoritative
        // Also true for the brief pointerless flash a keyboard move triggers
        // (`handleHandleKeyDown`) — one flag, one visual, for both inputs.
        const dragging = draggingKey === f.key
        // The meaning chooser belongs to the CARD's linkage, not to a field, so
        // it is rendered once — on the AUTHORITATIVE Definition (ADR-066
        // Decision 1's first field of a kind). The full-screen editor put a
        // control on every Definition field, which on a card with two of them
        // was one control claiming to be two: either moved the same single
        // `word_entry_id`. Definition is still where it sits (§14 item 1),
        // because it is the field the chosen sense fills.
        const senseAnchor = f.kind === 'definition' && f.key === definitionAnchorKey
        // Stated only once the server has answered: before that the band
        // cannot know which of its two states is true, and saying "no
        // dictionary link" for a card that has one would be a false claim.
        const senseState: 'chooser' | 'none' | null = !senseAnchor || !senses.data
          ? null
          : senseCandidates.length > 0 ? 'chooser' : 'none'
        // The authoritative Translation's provenance (§14 item 7) — shown only
        // where a chooser exists, since only there can linking change what the
        // field resolves from.
        const provenance = f.kind === 'translation' && f.key === translationAnchorKey && senseCandidates.length > 0
          ? translationProvenance(f.content, linkedSense?.translation)
          : null
        return (
          <div
            key={f.key}
            ref={(el) => { if (el) rowsRef.current.set(f.key, el); else rowsRef.current.delete(f.key) }}
            // `position: relative` is unconditional, not just while dragging:
            // the halo below is an always-mounted absolutely-positioned child
            // (only its OPACITY toggles, so it can transition), and it needs a
            // constant anchor rather than one that appears and disappears with
            // the state it is meant to be fading. `zIndex` stays conditional —
            // it exists only to keep the shadow, which bleeds past this row's
            // own box into the gap on either side, from being painted under
            // the next sibling down (ordinary siblings paint later-over-earlier
            // in DOM order at equal stacking, so an un-lifted row has nothing
            // to win that fight with and needs no z-index of its own).
            style={{ position: 'relative', zIndex: dragging ? 2 : undefined }}
          >
            {/* The grabbed-state halo (DESIGN_SYSTEM.md "Field drag"). Out of
                flow and negative-z within this row's own stacking context, so
                it can extend past the row's edges for visible shadow/tint
                without EVER touching the row's own border-box — the one thing
                that could trip DeckMobileFeed's ResizeObserver or contend with
                its placement motion (ADR-067 Decision D). Always mounted,
                opacity-only, so entry and exit both transition instead of
                popping; `pointerEvents: none` keeps it out of the drag's own
                hit-testing. */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute', inset: -5, zIndex: -1,
                borderRadius: 'var(--radius-md)',
                background: 'var(--accent-soft)',
                boxShadow: 'var(--shadow-float)',
                opacity: dragging ? 1 : 0,
                pointerEvents: 'none',
                transition: reduceMotion ? 'none' : `opacity ${FIELD_DRAG_LIFT_MS}ms ${FIELD_DRAG_EASING}`,
              }}
            />
            {/* Label band ABOVE the text area, always — deletion never sits
                inside the region where the caret is placed (§5.3). Present on
                every side-B row, not just the focused one, so removing a field
                never costs a focus change and a keyboard raise. Same for the
                handle: reordering needs to see every row at once, and a control
                that appeared on focus would make moving a field cost a focus
                change first. */}
            <div style={labelRowStyle} data-meaning-band={senseAnchor ? '' : undefined}>
              <button
                onPointerDown={(e) => handleDragStart(e, f)}
                onPointerMove={handleDragMove}
                onPointerUp={handleDragEnd}
                onPointerCancel={cancelDrag}
                // The compatibility mouse event fires despite pointerdown's
                // preventDefault in some engines, and it is the one that moves
                // focus. Both are stopped, so the caret never leaves the field.
                onMouseDown={(e) => e.preventDefault()}
                onKeyDown={(e) => handleHandleKeyDown(e, f)}
                aria-label={ce.reorderFieldAria(label)}
                // Reachable by keyboard (Shift/Ctrl+Tab-free navigation, screen
                // readers) but not a STATION on the path between fields — the
                // desktop cycle's ⇥ walks fields only (brief §22). Reordering
                // by keyboard still works via the handle's own Arrow Up/Down.
                tabIndex={-1}
                style={{
                  ...handleGutterStyle,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0, background: 'none', border: 'none',
                  // The gesture is a reorder and never also a scroll — the
                  // browser must not claim it for panning (ADR-067 Decision D).
                  touchAction: 'none',
                  cursor: dragging ? 'grabbing' : 'grab',
                  color: dragging ? 'var(--accent)' : 'var(--text-secondary)',
                  transition: reduceMotion ? 'none' : `color ${FIELD_DRAG_LIFT_MS}ms ${FIELD_DRAG_EASING}`,
                }}
              >
                <GripVertical size={14} strokeWidth={2} />
              </button>
              {f.isCustom ? (
                // The name IS the label, so it is edited in the label's own
                // place rather than through a dialog — the dashed underline is
                // the whole of the affordance, and the band does not change
                // shape when it is used. `tabIndex={-1}` for §22's reason and
                // one more of its own: this input is not registered in
                // `inputsRef`, so it never becomes `focusedKey`, and letting ⇥
                // fire from it would cross a card boundary against whichever
                // field held the caret last.
                <input
                  value={f.label}
                  onChange={(e) => setFieldLabel(f.key, e.target.value.slice(0, 50))}
                  onFocus={() => { labelBeforeRenameRef.current = { key: f.key, label: f.label } }}
                  onBlur={() => commitFieldLabel(f.key)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    // Enter finishes the name; Escape abandons it. Neither is a
                    // line break — a field name is one line by construction.
                    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
                    if (e.key === 'Escape') {
                      e.preventDefault()
                      const before = labelBeforeRenameRef.current
                      if (before && before.key === f.key) setFieldLabel(f.key, before.label)
                      e.currentTarget.blur()
                    }
                  }}
                  placeholder={ce.customFieldNamePlaceholder}
                  aria-label={ce.renameFieldAria(label || ce.customFieldLabel)}
                  tabIndex={-1}
                  style={customLabelInputStyle}
                />
              ) : (
                <span
                  style={{
                    ...eyebrowStyle,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    // The badge is the only thing that must not be pushed out, so
                    // the label is capped only where one can follow it (§5.3).
                    // A subordinate row carries no badge and a longer eyebrow, so
                    // it takes the flexible space instead of truncating at 96 px.
                    ...(subordinate
                      ? { flex: 1, minWidth: 0, opacity: 0.7 }
                      // On the anchor the capsule is what yields: it carries
                      // its own ellipsis, and the field's name must not be
                      // the thing a long gloss eats into.
                      : { maxWidth: 96, ...(senseAnchor ? { flexShrink: 0 } : {}) }),
                  }}
                >
                  {subordinate
                    ? `${label} · ${cb.ordinalPosition(ordinalInKind)} · ${cb.notStudiedLabel}`
                    : label}
                </span>
              )}
              {/* A renameable field keeps its subordination said out loud too —
                  it is the same ADR-066 fact, it just cannot live inside an
                  input's value. */}
              {f.isCustom && subordinate && (
                <span style={{ ...eyebrowStyle, opacity: 0.7, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {`· ${cb.ordinalPosition(ordinalInKind)} · ${cb.notStudiedLabel}`}
                </span>
              )}
              {provenance && (
                <span style={provenanceStyle}>
                  {`· ${provenance === 'link' ? cb.translationFromMeaning : cb.translationTyped}`}
                </span>
              )}
              {/* Only on a kind that ACTUALLY repeats (ADR-066 §2) — a card with
                  one Example stays quiet. This is the single place in the card
                  where the accent lands on a label. */}
              {authoritative && kindRepeats && (
                <span style={studiedBadgeStyle}>
                  <Check size={9} strokeWidth={3.2} />
                  {cb.studiedBadge}
                </span>
              )}
              {/* The rename input already absorbs the row's slack (it is the
                  one label that has to be wide enough to type into), so the
                  spacer stands down where it renders. */}
              <div style={{ flex: subordinate || f.isCustom ? 0 : 1 }} />
              {/* §14. The capsule is the only part of the chooser in flow, and
                  it is 22px inside this band's fixed 24px; the open list is
                  portalled out of the card entirely (see MeaningChooser). */}
              {senseState === 'chooser' && (
                <MeaningChooser
                  term={senses.data?.term ?? term}
                  candidates={senseCandidates}
                  linkedId={linkedId}
                  locked={senseLocked}
                  translationOnCard={hasValue(fields, 'translation')}
                  onChoose={linkSense}
                />
              )}
              {senseState === 'none' && <NoDictionaryLinkTag />}
              <button
                // The label band is not a tap target of its own, so the button
                // keeps its own click from reaching the card underneath.
                onClick={(e) => { e.stopPropagation(); deleteField(f) }}
                aria-label={ce.removeFieldAria(label)}
                // Same reasoning as the handle above: reachable, not a stop on
                // the field-to-field path. ⌥⌫ is the keyboard equivalent.
                tabIndex={-1}
                style={{
                  width: 24, height: 24, flexShrink: 0, padding: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', cursor: 'pointer',
                  // Grey by default, red on the focused row (§5.3).
                  color: focused ? 'var(--danger)' : 'var(--text-secondary)',
                }}
              >
                <Trash2 size={14} strokeWidth={2} />
              </button>
            </div>

            {attachment ? (
              <AttachmentField
                field={f}
                caption={cb.audioFieldCaption}
                replacing={replacingKey === f.key}
                onToggleReplace={() => setReplacingKey((k) => (k === f.key ? null : f.key))}
                onChange={(content) => setAttachmentContent(f.key, content)}
                labels={{
                  replace: cb.replaceAttachmentBtn,
                  cancel: cb.replaceAttachmentCancelBtn,
                  aria: cb.replaceAttachmentAria(label),
                }}
              />
            ) : (
              <GrowingField
                value={f.content}
                focused={focused}
                italic={f.kind === 'example'}
                label={label}
                placeholder={placeholderFor(f.kind, ce)}
                registerInput={(el) => { if (el) inputsRef.current.set(f.key, el); else inputsRef.current.delete(f.key) }}
                onChange={(v) => setFieldContent(f.key, v)}
                onFocus={() => handleFocus(f.key)}
                onBlur={() => handleBlur(f.key)}
              />
            )}
          </div>
        )
      })}

      {/* Brief §13 — a walk arrived here holding a field type this card has
          not got. The field's place is where it would be created: at the end of
          the side-B stack, immediately above the card's action row. Pressing it
          runs the SAME `addField` the `+ Field` chooser runs for that type, so
          the field it creates, where it lands, and the focus that follows are
          identical by construction rather than by agreement. */}
      {missingFieldType && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            addFieldRef.current(missingFieldType.kind, missingFieldType.label)
          }}
          style={missingFieldBtnStyle}
        >
          <Plus size={13} strokeWidth={2.2} />
          {dd.navAddMissingField(missingFieldType.displayName)}
        </button>
      )}

      {/* ── Card actions ── `+ Field` and `Fill` both act on this one card,
          so both live here rather than in the deck-scoped editing bar
          (ADR-067 Decision B). Neither is ever disabled: a control that only
          renders while its card is active has no "no active card" state left
          to represent. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFieldChooser() }}
          aria-label={dd.barAddFieldAria}
          aria-expanded={fieldChooserOpen}
          style={cardActionBtnStyle(fieldChooserOpen)}
        >
          <Plus size={13} strokeWidth={2.2} />
          {dd.barAddField}
        </button>
        {showFillTrigger && (
          <button
            onClick={(e) => { e.stopPropagation(); handleFill() }}
            disabled={fillMutation.isPending}
            style={cardActionBtnStyle(false, fillMutation.isPending)}
          >
            {fillMutation.isPending ? cb.fillLoading : cb.fillBtn}
          </button>
        )}
      </div>
      {fillError && (
        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--danger)' }}>{fillError}</div>
      )}
    </div>
  )
}

const ActiveVocabCard = forwardRef(ActiveVocabCardImpl)
export default ActiveVocabCard

// Fill only ever adds a field that was missing (ADR-063 — never overwrites
// user-entered content), so the merge rule is narrow on purpose: an existing
// blank field of the same kind is filled in place (keeping its key, so no
// re-render churn), and only if no field of that kind already carries content
// is a new one appended. A field the user has since typed into is left alone
// even though Fill does not know that — the response can only arrive after
// the request, and by then the draft may have moved on.
function mergeFilledSides(existing: FieldDraft[], sides: Card['sides']): FieldDraft[] {
  const incoming = draftsFromCardSides(sides).filter((f) => f.content.trim())
  const next = [...existing]
  for (const inc of incoming) {
    const blankIdx = next.findIndex((f) => f.kind === inc.kind && !f.content.trim())
    // The row's own key is kept, as this comment has always said it was: the
    // filled field is the same row, and taking the server's id into the key
    // slot would remount it. Its IDENTITY is the server's — that is what `inc`
    // is carrying, and what makes the filled field reconcilable afterwards.
    if (blankIdx >= 0) { next[blankIdx] = { ...inc, key: next[blankIdx].key }; continue }
    if (!next.some((f) => f.kind === inc.kind && f.content.trim())) next.push(inc)
  }
  return next
}

/**
 * One inline text field of the ACTIVE card. It is always the height of its own
 * content — brief §8 ("активное: поле растёт по содержимому, собственной
 * прокрутки не имеет") and §22 for desktop, neither of which carries a focus
 * condition — and it never scrolls internally, because the page is the only
 * scrolling surface (ADR-067 Decision 3): no max height, no `overflow: auto`.
 *
 * Height must not depend on focus, and that is the point of this comment rather
 * than an incidental property. It used to: an unfocused field was clamped to one
 * row and grew only once it took the caret. That made the card's height change
 * as a CONSEQUENCE of the very focus change that triggers positioning, so the
 * placement was computed against geometry that was already stale — and being
 * single-shot (Decision A: the card is positioned once, on activation), it was
 * never corrected. Tapping back and forth between two fields therefore walked
 * the card into place over many taps instead of landing in one. Restoring the
 * specified behaviour removes the cause; nothing in the positioning arithmetic
 * changed, and nothing here equalises, pads or clamps a height to make the
 * neighbour invariant hold, which the sharpening amendment forbids by name.
 *
 * The `+ borders` term is not a fudge: `scrollHeight` on a `border-box` element
 * reports content + padding and excludes the border, so setting it alone as the
 * height would clip the last line by the border width. Added back, the pair
 * reduces to the element's own border-box height at `auto` — which is why the
 * result is the same integer in both focus states rather than merely close to
 * it. Border and padding are the same in both focus states (the focus ring is
 * a box-shadow — see `fieldBoxStyle`), so the chrome is 22 px either way by
 * construction, not by a compensation the browser's border snapping can undo.
 */
function GrowingField({
  value, focused, italic, label, placeholder, registerInput, onChange, onFocus, onBlur,
}: {
  value: string
  focused: boolean
  italic: boolean
  label: string
  placeholder: string
  registerInput: (el: HTMLTextAreaElement | null) => void
  onChange: (v: string) => void
  onFocus: () => void
  onBlur: () => void
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)

  // Layout, not passive: the height is settled before the browser paints, so a
  // field never renders at one row and then jumps. It also means the size is
  // final before ActiveVocabCard's mount effect places the caret and before
  // DeckMobileFeed measures for placement — child layout effects run ahead of
  // both — which is what makes the single-shot placement read true geometry.
  // `focused` is deliberately NOT a dependency: the height does not depend on it.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const borders = el.offsetHeight - el.clientHeight
    el.style.height = `${el.scrollHeight + borders}px`
  }, [value])

  return (
    <textarea
      ref={(el) => { ref.current = el; registerInput(el) }}
      // One row is the FLOOR, not a clamp: the layout effect above always
      // overwrites the height with the content's own, and this only says what
      // an empty field measures.
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      aria-label={label}
      style={{
        ...fieldBoxStyle(focused),
        resize: 'none', overflow: 'hidden',
        fontStyle: italic ? 'italic' : 'normal',
        fontSize: 15, lineHeight: 1.5,
      }}
    />
  )
}

/**
 * Image and audio are file actions, not typing, and cost ONE ROW at rest like
 * any other field (§5.3) — the chip below is that row, and it is why the widget
 * is not simply rendered open: `ImageSideInput` puts a preview of up to 180 px
 * plus an action strip on the card, which is three or four rows' worth of
 * height for a field the author is usually only passing.
 *
 * `Replace` opens the widget beneath the chip, and the widget is the SAME
 * `ImageSideInput` / `AudioSideInput` the retired full-screen editor used — not
 * a reimplementation, so file size caps, compression, URL validation and the
 * recorder all behave identically to where this capability came from.
 * `ActiveVocabCard`'s previous comment here said changing the file "stays in
 * the full-screen editor until the hosted-images debt is paid (§18)": that debt
 * is still unpaid and is untouched, but the editor is no longer somewhere to
 * defer to, which is what made the deferral expire rather than the debt.
 *
 * An empty attachment opens straight into the widget: a chip for a file that is
 * not there yet would be an empty frame with nothing to say.
 */
function AttachmentField({
  field, caption, replacing, onToggleReplace, onChange, labels,
}: {
  field: FieldDraft
  caption: string
  replacing: boolean
  onToggleReplace: () => void
  onChange: (content: string) => void
  labels: { replace: string; cancel: string; aria: string }
}) {
  const empty = !field.content.trim()
  const open = replacing || empty
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!empty && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          {field.kind === 'image' ? (
            <img
              src={field.content}
              alt=""
              style={{ width: 34, height: 26, objectFit: 'cover', borderRadius: 5, border: '1px solid var(--border)', flexShrink: 0 }}
            />
          ) : (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'var(--bg-page)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)', padding: '6px 12px',
            }}>
              <Music size={13} strokeWidth={2} color="var(--text-secondary)" />
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)' }}>{caption}</span>
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleReplace() }}
            aria-label={labels.aria}
            aria-expanded={replacing}
            // Not a stop on the field-to-field walk, for the same reason the
            // handle and the trash are not (brief §22).
            tabIndex={-1}
            style={replaceBtnStyle(replacing)}
          >
            {replacing ? labels.cancel : labels.replace}
          </button>
        </div>
      )}
      {open && (
        <div onClick={(e) => e.stopPropagation()}>
          {field.kind === 'image'
            ? <ImageSideInput content={field.content} onChange={onChange} />
            : <AudioSideInput content={field.content} onChange={onChange} />}
        </div>
      )}
    </div>
  )
}

function replaceBtnStyle(active: boolean): CSSProperties {
  return {
    flexShrink: 0,
    display: 'inline-flex', alignItems: 'center',
    padding: '4px 11px',
    background: active ? 'var(--accent-soft)' : 'transparent',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-pill)',
    color: 'var(--accent)', cursor: 'pointer',
    fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 11.5,
  }
}

type CardEditorLabels = AppTranslations['cardEditor']

const DISPLAY_LABEL = (ce: CardEditorLabels): Record<string, string> => ({
  translation: ce.translationLabel, definition: ce.definitionLabel, example: ce.exampleLabel,
  note: ce.noteLabel, image: ce.imageLabel, audio: ce.audioLabel, link: ce.linkLabel,
  dialog: ce.dialogLabel,
})

function placeholderFor(kind: string, ce: CardEditorLabels): string {
  switch (kind) {
    case 'translation': return ce.translationPlaceholder
    case 'definition': return ce.definitionPlaceholder
    case 'example': return ce.examplePlaceholder
    case 'link': return ce.linkPlaceholder
    case 'dialog': return ce.dialogPlaceholder
    default: return ce.notePlaceholder
  }
}

// 24 px band; with the 8 px gap below it and the field frame beneath, the label
// and the trash reach the 44 px vertical target between them (§5.3). The handle
// sits in the same band and inherits the same 44 px.
const labelRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, height: 24, marginBottom: 4,
  // The height is FIXED, not a minimum: §14 item 4 — the meaning capsule, its
  // locked form and the no-link tag all sit inside it, and none of them may
  // change the card's height under a focused field (ADR-067 Decision A).
  flexShrink: 0,
}

// §5.3's narrowest row, at 334 px of internal width: handle 15, gap 7, label up
// to 96 with an ellipsis, the flexible gap, trash 24 — 149 px of fixed width,
// so the label truncates long before the trash can be pushed anywhere. Both the
// handle and Term's empty gutter take this exact box, which is what puts every
// label on one vertical line and makes Term's missing handle a visible absence
// rather than a differently-shaped row. `flexShrink: 0` is load-bearing, not
// defensive: without it the flex line would take the handle's width back from
// it before it took any from the label's own max.
const handleGutterStyle: CSSProperties = {
  width: 15, height: 24, flexShrink: 0,
}

// The one place in the card where the accent lands on a field's label rather
// than on the field itself (§5.2/§5.3). `flexShrink: 0` because §5.3's tight-row
// arithmetic gives it the last word: the label truncates, the badge does not.
const studiedBadgeStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
  background: 'var(--accent-soft)', color: 'var(--accent)',
  borderRadius: 'var(--radius-pill)', padding: '2px 8px',
  fontFamily: 'var(--font-ui)', fontSize: 9, fontWeight: 700,
  letterSpacing: '0.07em', textTransform: 'uppercase',
}

const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.09em',
  color: 'var(--text-secondary)', textTransform: 'uppercase',
}

// A custom field's name, in the label's own place. Styled as the eyebrow it
// replaces — same size, weight and colour — so the band keeps its shape and the
// row does not announce itself as a different kind of row. The dashed underline
// is the whole of the affordance: it says "this text is yours" without spending
// a control on saying it.
const customLabelInputStyle: CSSProperties = {
  flex: 1, minWidth: 40,
  fontFamily: 'var(--font-ui)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: 'var(--text-secondary)', background: 'transparent',
  border: 'none', borderBottom: '1px dashed var(--border)',
  padding: '2px 0', outline: 'none',
}

// The Translation label's provenance (§14 item 7). Beside the eyebrow and
// quieter than it — the label keeps its rank; this is a note on it, in
// sentence case so it reads as a phrase rather than as a second label.
const provenanceStyle: CSSProperties = {
  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  fontFamily: 'var(--font-ui)', fontSize: 10.5, fontWeight: 500,
  color: 'var(--text-secondary)',
}

// `+ Field` and `Fill`, the row at the end of the card's field stack (ADR-067
// Decision B). Neither ever renders disabled: `+ Field` has no "no active
// card" state to represent here (this component only exists while its card
// is active), and Fill's own pending state dims and relabels rather than
// disabling in the dashed style the old bar used for a genuinely unavailable
// control — this one is always available, just briefly busy. `open` accents
// the trigger while its popover is showing, the same toggle-state convention
// the editing bar used before Decision B moved it here.
// §13's add-affordance stands where the missing field would be, so it is
// field-width and dashed — an outline of the thing it would create, rather than
// another pill in the action row below it, which is a different kind of control
// (a deck-authoring action, not a placeholder for content).
const missingFieldBtnStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  width: '100%', padding: '11px 14px', marginTop: 2,
  background: 'transparent',
  border: '1px dashed var(--border-active)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--accent)',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12.5,
}

function cardActionBtnStyle(open: boolean, pending = false): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: open ? 'var(--accent-soft)' : 'transparent',
    border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-pill)', padding: '6px 14px',
    color: 'var(--accent)',
    opacity: pending ? 0.7 : 1,
    cursor: pending ? 'default' : 'pointer',
    fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12.5,
  }
}

// The field frame reads as a well INSIDE the card in both themes: --bg-page is
// lighter-grey against white in light and darker against #161B22 in dark, so
// neither theme leaves the frame invisible (the dark collision noted in the
// brief's open tail #3 is between --bg-header and --bg-surface, not this pair).
//
// Focus changes paint, never layout. The border is 1px in both states and the
// focus ring's extra weight is an inset box-shadow, which takes no part in
// layout. It used to be a 1.5px border with the extra half-pixel taken back out
// of the padding — a compensation that holds only if the border renders at its
// specified width, and Chromium snaps border widths down (1.5px rendered as 1px
// at every density measured, 1 through 2), so the focused field came out a pixel
// short: the Term input on focus, and a text field on its first keystroke,
// which `GrowingField` then kept, clipping its last line. The card's height is
// what a focused field is positioned against (DESIGN_SYSTEM.md §14 item 4);
// with no layout property varying by focus, nothing here can move it.
function fieldBoxStyle(focused: boolean): CSSProperties {
  return {
    width: '100%', boxSizing: 'border-box',
    fontFamily: 'var(--font-content)', color: 'var(--text-primary)',
    background: focused ? 'var(--accent-soft)' : 'var(--bg-page)',
    border: `1px solid ${focused ? 'var(--accent)' : 'var(--border)'}`,
    boxShadow: focused ? 'inset 0 0 0 0.5px var(--accent)' : 'none',
    borderRadius: 8,
    padding: '10px 12px',
    outline: 'none',
  }
}
